import type { KeySlot } from "./browser-key.js";

/**
 * The admin sessions this browser holds: one for mainnet, one for Nile (docs/product/admin-auth.md §4).
 *
 * The token is kept in localStorage, and that is safe on purpose: on its own it opens nothing. Every request must also
 * be signed by this browser's non-extractable key (browser-key.ts), so a token read out of storage by an extension or a
 * script cannot be replayed from anywhere else. What is stored is what the API answered at sign-in.
 */

export type AdminSessionRole = "viewer" | "editor" | "admin" | "root";
export type AdminSignInMethod = "tron" | "ethereum" | "passkey";

export interface StoredAdminSession {
  token: string;
  address: string;
  role: AdminSessionRole;
  method: AdminSignInMethod;
  expiresAt: string;
}

const keyOf = (slot: KeySlot) => `tenergy-admin.session.${slot}`;
const memory = new Map<KeySlot, StoredAdminSession>();
const listeners = new Set<(slot: KeySlot) => void>();

function storage(): Storage | null {
  try {
    return typeof localStorage === "undefined" ? null : localStorage;
  } catch {
    return null;
  }
}

/**
 * Read from localStorage on EVERY call, never from a per-tab copy: the browser key in IndexedDB is shared by every tab,
 * so the token must be too. A tab holding its own older token signed with the newer key another tab's sign-in made, and
 * the API refused it (2026-10-31). The in-memory map is only for a browser that refuses storage.
 */
export function readAdminSession(slot: KeySlot): StoredAdminSession | null {
  const store = storage();
  if (store === null) return memory.get(slot) ?? null;
  try {
    const raw = store.getItem(keyOf(slot));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredAdminSession;
    if (typeof parsed.token !== "string" || typeof parsed.address !== "string") return null;
    if (Date.parse(parsed.expiresAt) <= Date.now()) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function writeAdminSession(slot: KeySlot, session: StoredAdminSession): void {
  memory.set(slot, session);
  try {
    storage()?.setItem(keyOf(slot), JSON.stringify(session));
  } catch {
    // A browser that refuses storage keeps the session for this tab only.
  }
  for (const listener of listeners) listener(slot);
}

/** Root's token is re-signed on use (sliding idle window); the new one replaces the old in place. */
export function renewAdminToken(slot: KeySlot, token: string): void {
  const current = readAdminSession(slot);
  if (current !== null && current.token !== token) writeAdminSession(slot, { ...current, token });
}

/**
 * Forget the session of `slot`. With `onlyToken`, only when that is still the stored token: a request refused for an
 * old token must not sign out a newer session that another tab has opened since.
 */
export function clearAdminSession(slot: KeySlot, onlyToken?: string): void {
  const current = readAdminSession(slot);
  if (onlyToken !== undefined && current !== null && current.token !== onlyToken) return;
  const had = memory.has(slot) || current !== null;
  memory.delete(slot);
  try {
    storage()?.removeItem(keyOf(slot));
  } catch {
    // nothing stored
  }
  if (had) for (const listener of listeners) listener(slot);
}

export function onAdminSessionChange(listener: (slot: KeySlot) => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

const ADDRESS_KEY = "tenergy-admin.address";

/** The address last signed in here: a convenience for the passkey sign-in, never a credential. */
export function rememberedAdminAddress(): string | null {
  try {
    return storage()?.getItem(ADDRESS_KEY) ?? null;
  } catch {
    return null;
  }
}

export function rememberAdminAddress(address: string): void {
  try {
    storage()?.setItem(ADDRESS_KEY, address);
  } catch {
    // storage refused: the passkey sign-in then needs a wallet sign-in first
  }
}

/** Test seam. */
export function resetAdminSessions(): void {
  memory.clear();
  try {
    storage()?.removeItem(keyOf("mainnet"));
    storage()?.removeItem(keyOf("nile"));
  } catch {
    // nothing stored
  }
}
